import { useEffect, useState } from 'react';

import type { FormCountrySelectCountry } from '@coingate/ui/dist/components/FormCountrySelect';
import { Request } from '@javascript/helpers';
import DualPaneLayout from '@shared/components/DualPaneLayout';
import SinglePaneLayout from '@shared/components/SinglePaneLayout';
import { getErrorMessage, getFieldErrors } from '@shared/errors';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import PasswordStep from '@shared/password/PasswordStep';

import type { ProfileSetUpSteps } from './constants';
import { PROFILE_SET_UP_STEP_CONFIG } from './constants';
import { clearInvitationId } from './invitation';
import AboutYourBusinessStep from './profile/components/AboutYourBusinessStep';
import AboutYouStep from './profile/components/AboutYouStep';
import StepContent from './profile/components/StepContent';
import type { AboutYouFormType, AboutYourBusinessFormType, PasswordFormType } from './validations';

interface CreateProfileProps {
  countries: FormCountrySelectCountry[];
  skipBusinessStep: boolean;
  invitationId: string | null;
}

const CreateProfile = ({ countries, skipBusinessStep, invitationId }: CreateProfileProps) => {
  const { trackEvent } = useAnalytics('authentication');

  const confirmationToken = new URLSearchParams(window.location.search).get('confirmation_token');

  const [currentStep, setCurrentStep] = useState<ProfileSetUpSteps>('password');
  const [stepHistory, setStepHistory] = useState<ProfileSetUpSteps[]>(['password']);
  const [passwordData, setPasswordData] = useState<PasswordFormType | undefined>();
  const [aboutYouData, setAboutYouData] = useState<AboutYouFormType | undefined>();
  const [aboutYourBusinessData, setAboutYourBusinessData] = useState<AboutYourBusinessFormType | undefined>();

  useEffect(() => {
    trackEvent('page_view', { page: `create_profile_${currentStep}` });
  }, [currentStep, trackEvent]);

  const goToStep = (step: ProfileSetUpSteps) => {
    setStepHistory([...stepHistory, step]);
    setCurrentStep(step);
  }

  const handleBack = () => {
    trackEvent('create_profile_back_click', { from_step: currentStep });

    const newHistory = [...stepHistory];
    newHistory.pop();

    const previousStep = newHistory[newHistory.length - 1];

    setStepHistory(newHistory);
    setCurrentStep(previousStep);
  }

  const handlePasswordContinue = async (data: PasswordFormType) => {
    trackEvent('create_profile_password_submit');

    setPasswordData(data);

    try {
      await Request(
        `/users/confirmation/validate_password`,
        'POST',
        {
          body: {
            password: {
              password: data.password,
              password_confirmation: data.password,
            },
          },
        }
      );
    } catch (err: any) {
      trackEvent('create_profile_password_submit_failed', { error: err });

      return { error: getErrorMessage(err, 'password') };
    }

    trackEvent('create_profile_password_submit_success');

    goToStep('aboutYou');
  };

  const handleAboutYouContinue = async (data: AboutYouFormType) => {
    trackEvent('create_profile_about_you_submit');

    setAboutYouData(data);

    try {
      await Request(
        `/users/confirmation/validate_about`,
        'POST',
        {
          body: {
            user: {
              first_name: data.firstName,
              last_name: data.lastName,
            },
          },
        }
      );
    } catch (err: any) {
      trackEvent('create_profile_about_you_submit_failed', { error: err });

      return { fieldErrors: getFieldErrors(err, { first_name: 'firstName', last_name: 'lastName' }) };
    }

    trackEvent('create_profile_about_you_submit_success');

    if (skipBusinessStep) {
      return submitConfirmation(data);
    }

    goToStep('aboutYourBusiness');
  };

  const handleAboutYourBusinessContinue = async (data: AboutYourBusinessFormType) => {
    trackEvent('create_profile_about_business_submit');

    setAboutYourBusinessData(data);

    const result = await submitConfirmation(aboutYouData, data);

    if (result) {
      return result;
    }
  };

  const stepComponent = {
    password: () => (
      <PasswordStep
        defaultValues={passwordData}
        onContinue={handlePasswordContinue}
      />
    ),
    aboutYou: () => (
      <AboutYouStep
        defaultValues={aboutYouData}
        onContinue={handleAboutYouContinue}
      />
    ),
    aboutYourBusiness: () => (
      <AboutYourBusinessStep
        countries={countries}
        defaultValues={aboutYourBusinessData}
        onContinue={handleAboutYourBusinessContinue}
      />
    ),
  };

  const submitConfirmation = async (aboutData: AboutYouFormType, businessData?: AboutYourBusinessFormType) => {
    const body: Record<string, unknown> = {
      password: {
        password: passwordData.password,
        password_confirmation: passwordData.password,
      },
      user: {
        first_name: aboutData.firstName,
        last_name: aboutData.lastName,
      },
    };

    if (businessData) {
      body.business = {
        business_name: businessData.businessName,
        country: businessData.country,
      };
    }

    if (invitationId) {
      body.invitation_id = invitationId;
    }

    try {
      const response = await Request(
        `/users/confirmation?confirmation_token=${confirmationToken}`,
        'PATCH',
        { body }
      );

      trackEvent('create_profile_submit_success');

      clearInvitationId();
      window.location.href = response.redirect_url;
    } catch (err: any) {
      trackEvent('create_profile_submit_failed', { error: err });

      return {
        fieldErrors: getFieldErrors(err, {
          first_name: 'firstName',
          last_name: 'lastName',
          business_name: 'businessName',
          country: 'country',
        }),
      };
    }
  };

  const currentLayout = PROFILE_SET_UP_STEP_CONFIG[currentStep].layout;

  const content = (
    <StepContent
      title={PROFILE_SET_UP_STEP_CONFIG[currentStep].title}
      currentStep={currentStep}
      stepComponent={stepComponent}
      stepHistory={stepHistory}
      onBack={handleBack}
    />
  );

  if (currentLayout === 'single') {
    return (
      <SinglePaneLayout content={content} />
    );
  }

  return (
    <DualPaneLayout leftContent={content} />
  );
}

export default CreateProfile
